// firmwareApi.ts — typed wrapper over useApi for the Firmware Hub.
//
// Downloads go through the gated backend proxy (GET /firmware/:id/download),
// not a public Drive link — so we fetch the raw Response (skipJsonParse), turn
// it into a Blob, and trigger a browser download with the build's filename.

import { useApi } from '../apiHelpers';
import type { Firmware, FirmwareFile, FirmwareListResponse, FirmwareMessage } from './types';

export interface PublishFields {
  category: string;
  label: string;
  version?: string;
  git_hash?: string;
  notes?: string;
  branch?: string;
  architecture?: string;
  release_date?: string; // YYYY-MM-DD
  applicable_models: string[];
  is_active?: boolean;
  file: File;
  sig?: File | null; // optional detached signature for this file
}

export interface UpdateFields {
  label?: string;
  version?: string | null;
  notes?: string | null;
  git_hash?: string | null;
  branch?: string | null;
  architecture?: string | null;
  release_date?: string | null;
  applicable_models?: string[];
  is_active?: boolean;
  is_archived?: boolean;
}

export interface UpdateFileFields {
  architecture?: string | null;
  applicable_models?: string[];
}

// Fetch through the gated proxy, then trigger a browser download under the
// server-side filename. Shared by the firmware image and its .sig.
async function downloadVia(
  api: ReturnType<typeof useApi>,
  path: string,
  fileName: string,
): Promise<void> {
  const res: Response = await api.get(path, { skipJsonParse: true });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function useFirmwareApi() {
  const api = useApi();

  return {
    list: (): Promise<FirmwareListResponse> => api.get('/firmware'),

    publish: (fields: PublishFields): Promise<Firmware & { file: FirmwareFile; attached: boolean; addedTarget: boolean }> => {
      const fd = new FormData();
      fd.append('file', fields.file);
      if (fields.sig) fd.append('sig', fields.sig);
      fd.append('category', fields.category);
      fd.append('label', fields.label);
      if (fields.version) fd.append('version', fields.version);
      if (fields.git_hash) fd.append('git_hash', fields.git_hash);
      if (fields.notes) fd.append('notes', fields.notes);
      if (fields.branch) fd.append('branch', fields.branch);
      if (fields.architecture) fd.append('architecture', fields.architecture);
      if (fields.release_date) fd.append('release_date', fields.release_date);
      fd.append('applicable_models', JSON.stringify(fields.applicable_models));
      fd.append('is_active', String(!!fields.is_active));
      return api.post('/firmware', { body: fd });
    },

    update: (id: number, patch: UpdateFields): Promise<Firmware> =>
      api.patch(`/firmware/${id}`, { body: patch }),

    // ── per-target-file operations ─────────────────────────────────────────
    updateFile: (fileId: number, patch: UpdateFileFields): Promise<FirmwareFile> =>
      api.patch(`/firmware/files/${fileId}`, { body: patch }),

    // Remove ONE target from a build. The server refuses (409) on the build's
    // last file — retire or delete the build instead.
    removeFile: (fileId: number): Promise<{ id: number; deleted: boolean }> =>
      api.delete(`/firmware/files/${fileId}`),

    // Attach or replace the detached .sig on one target file.
    uploadSignature: (fileId: number, sig: File): Promise<FirmwareFile> => {
      const fd = new FormData();
      fd.append('sig', sig);
      return api.post(`/firmware/files/${fileId}/signature`, { body: fd });
    },

    removeSignature: (fileId: number): Promise<FirmwareFile> =>
      api.delete(`/firmware/files/${fileId}/signature`),

    downloadFile: (file: FirmwareFile): Promise<void> =>
      downloadVia(api, `/firmware/files/${file.id}/download`, file.file_name || `firmware-${file.id}`),

    downloadSignature: (file: FirmwareFile): Promise<void> =>
      downloadVia(api, `/firmware/files/${file.id}/signature/download`, file.sig_file_name || `firmware-${file.id}.sig`),

    // Permanent delete (manager/admin) — removes the Drive file, row, and thread.
    remove: (id: number): Promise<{ id: number; deleted: boolean }> =>
      api.delete(`/firmware/${id}`),

    // Resolve a build by git hash OR sha256 prefix (>=7 hex chars). Powers the
    // shareable /firmware/:hash page, the search box, and drop-to-find. silent:
    // a 404 here is an expected "not found", surfaced by the caller's UI.
    // `slug` scopes the match to one category — the namespaced
    // /firmware/dronevis/:hash page passes it; everything else stays blind.
    resolveByHash: (hash: string, slug?: string): Promise<Firmware> =>
      api.get(
        slug
          ? `/firmware/by-hash/${encodeURIComponent(slug)}/${encodeURIComponent(hash)}`
          : `/firmware/by-hash/${encodeURIComponent(hash)}`,
        { silent: true },
      ),

    // Append-only comment thread per build. Body key `message` matches the
    // event/tour thread convention on the backend.
    // silent: a thread is a best-effort side panel, so a failed load shouldn't
    // spam the console (matches EventMessages). The UI still surfaces a toast.
    listMessages: (id: number): Promise<FirmwareMessage[]> => api.get(`/firmware/${id}/messages`, { silent: true }),
    postMessage: (id: number, message: string): Promise<FirmwareMessage> =>
      api.post(`/firmware/${id}/messages`, { body: { message } }),

    // Build-level download: serves the build's PRIMARY file via the legacy alias.
    // Prefer downloadFile() so a multi-target build downloads the right target.
    download: (fw: Firmware): Promise<void> =>
      downloadVia(api, `/firmware/${fw.id}/download`, fw.file_name || `firmware-${fw.id}`),
  };
}

// ── display helpers ────────────────────────────────────────────────────────

export function formatBytes(bytes: number | string | null | undefined): string {
  const n = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (!n || Number.isNaN(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function shortSha(sha: string | null | undefined): string {
  if (!sha) return '';
  return `${sha.slice(0, 6)}…${sha.slice(-4)}`;
}

// The short hash shown in the UI and used in the shareable /firmware/:hash link:
// the git short hash if present, else a sha256 prefix. Mirrors the server's
// shortHashFor() so the link, the filename, and the chip all agree.
export function firmwareShortHash(fw: { git_hash?: string | null; sha256?: string | null }): string {
  const gh = (fw.git_hash || '').trim();
  if (gh) return gh.slice(0, 7);
  return (fw.sha256 || '').slice(0, 7);
}

// Body text for the "delete firmware permanently" confirm — shared by the
// Download tab + detail page. Newlines render via the modal's whitespace-pre-wrap.
export function targetName(file: { architecture: string | null }): string {
  return (file.architecture || '').trim() || 'Unspecified target';
}

export function firmwareDeleteMessage(
  fw: { label: string; file_name: string | null; category: string; git_hash: string | null; files?: { file_name: string }[] },
): string {
  const names = fw.files?.length ? fw.files.map((f) => f.file_name) : [fw.file_name].filter(Boolean);
  const head = [fw.label, ...names].filter(Boolean).join('\n');
  const count = names.length;
  const body = count > 1
    ? `This removes all ${count} target files (and any signatures), this record, and its entire comment thread. It cannot be undone.`
    : 'This removes the build file (and any signature), this record, and its entire comment thread. It cannot be undone.';
  const telemetry =
    fw.category === 'px4' && fw.git_hash
      ? '\n\nThis PX4 build also feeds the Statistics telemetry charts — deleting it removes it there too.'
      : '';
  return `${head}\n\n${body}${telemetry}`;
}

export function formatUploaded(ts: string | null | undefined): string {
  if (!ts) return '';
  // uploaded_at is a full timestamptz, so new Date() is safe here (no bare-DATE
  // timezone-shift gotcha — that only affects YYYY-MM-DD columns).
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
